import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { statsApi } from '../services/api';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Loading from '../components/Loading';
import './StatsForNerds.css';

function StatsForNerds() {
  const navigate = useNavigate();
  const { user, authLoading } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!authLoading && (!user || user.profile !== 'admin')) {
      navigate('/admin');
      return;
    }
    if (user) {
      loadStats();
    }
  }, [user, authLoading, navigate]);

  const loadStats = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await statsApi.getSubmissionStats();
      if (response.data.success) {
        setSubmissions(response.data.data || []);
      } else {
        setError(response.data.message || 'Failed to load statistics');
      }
    } catch (err) {
      console.error('Error loading stats:', err);
      setError(err.response?.data?.message || 'Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || !user) {
    return (
      <>
        <Header />
        <div className="container">
          <Loading />
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="container">
        <div className="stats-for-nerds-page">
          <div className="admin-header">
            <h1>Stats for Nerds</h1>
            <Link to="/admin" className="btn btn-secondary back-btn">
              <i className="bi bi-arrow-left"></i> Back to GR Admin
            </Link>
          </div>
          
          <p className="page-description">
            Detailed statistics and analytics for rule submissions and voting patterns.
            More statistics will be available in the future.
          </p>

          {error && <div className="alert alert-danger">{error}</div>}

          {loading ? (
            <Loading />
          ) : (
            <>
              <div className="stats-section">
                <h2>Submission Statistics</h2>
                <div className="stats-table-container">
                  <table className="table table-striped table-hover stats-table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Submitted By</th>
                        <th>Rule Title</th>
                        <th>Status</th>
                        <th>Date</th>
                        <th>Yes</th>
                        <th>No</th>
                        <th>Total</th>
                        <th>Yes %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {submissions.length === 0 ? (
                        <tr>
                          <td colSpan="9" className="text-center">No submissions found</td>
                        </tr>
                      ) : (
                        submissions.map((submission) => (
                          <tr key={submission.ID || submission.submit_id}>
                            <td>{submission.ID || submission.submit_id}</td>
                            <td>{submission['Submitted By'] || submission.name}</td>
                            <td className="rule-title-cell">
                              {submission['Rule Title'] || submission.title}
                            </td>
                            <td>
                              <span className={`status-badge status-${(submission.Status || submission.status || '').toLowerCase()}`}>
                                {submission.Status || submission.status}
                              </span>
                            </td>
                            <td>{submission.Date || submission.date}</td>
                            <td className="vote-count yes">{submission.Yes || submission.yes_count || 0}</td>
                            <td className="vote-count no">{submission.No || submission.no_count || 0}</td>
                            <td className="vote-count total">{submission.Total || submission.total_votes || 0}</td>
                            <td className="vote-percentage">{submission['Yes %'] || submission.yes_percentage || '0%'}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="stats-summary">
                <h3>Summary</h3>
                <div className="summary-grid">
                  <div className="summary-card">
                    <div className="summary-label">Total Submissions</div>
                    <div className="summary-value">{submissions.length}</div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-label">Total Votes</div>
                    <div className="summary-value">
                      {submissions.reduce((sum, s) => sum + (parseInt(s.Total || s.total_votes || 0)), 0)}
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-label">Total Yes Votes</div>
                    <div className="summary-value yes">
                      {submissions.reduce((sum, s) => sum + (parseInt(s.Yes || s.yes_count || 0)), 0)}
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="summary-label">Total No Votes</div>
                    <div className="summary-value no">
                      {submissions.reduce((sum, s) => sum + (parseInt(s.No || s.no_count || 0)), 0)}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
}

export default StatsForNerds;
